import { useFollowedSite } from '../../../contexts/SiteContext';
import { useEffect, useMemo, useState } from 'react';
import { Segmented, OptionChip } from '@verlo/shared/components/ui/segmented';
import { RefreshCw, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { SitePicker } from '@verlo/shared/components/ui/site-picker';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@verlo/shared/components/ui/sheet';
import { Button } from '@verlo/shared/components/ui/button';
import { Input } from '@verlo/shared/components/ui/input';
import { reportsIntelApi } from '@verlo/shared/services/api';
import ReportsLayout from '../../../components/reports/ReportsLayout';
import KpiCard from '../../../components/reports/KpiCard';
import AlertsBanner from '../../../components/reports/AlertsBanner';
import MorningBriefCard from '../../../components/reports/MorningBriefCard';
import VarianceBriefCard from '../../../components/reports/VarianceBriefCard';
import DrillPanelContent from '../../../components/reports/DrillPanelContent';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@verlo/shared/components/ui/dialog';

const RANGES = [
  { key: 'today', label: 'Today' },
  { key: 'wtd',   label: 'WTD' },
  { key: 'mtd',   label: 'MTD' },
  { key: 'ytd',   label: 'YTD' },
];

const RANGE_LABEL = Object.fromEntries(RANGES.map((r) => [r.key, r.label]));

const ExecutiveDashboardPage = () => {
  const [sites, setSites] = useState([]);
  // '' = every site this user can read; the API omits the param (S1-4).
  const [site, setSite] = useFollowedSite('');
  const [range, setRange] = useState('today');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [presets, setPresets] = useState([]);
  const [activePresetId, setActivePresetId] = useState('default');
  const [drillKpi, setDrillKpi] = useState(null);
  const [presetDialogOpen, setPresetDialogOpen] = useState(false);
  const [newPresetName, setNewPresetName] = useState('');
  const [pinnedViews, setPinnedViews] = useState([]);

  const siteLabel = useMemo(() => sites.find((s) => s.id === site)?.name || 'All sites', [sites, site]);

  // Initial load — sites + presets + pinned views
  useEffect(() => {
    Promise.all([
      reportsIntelApi.listSites(),
      reportsIntelApi.listPresets(),
      reportsIntelApi.listPinnedViews().catch(() => ({ data: { views: [] } })),
    ])
      .then(([s, p, pv]) => {
        setSites(s.data.sites || []);
        setPresets(p.data.presets || []);
        setPinnedViews(pv.data.views || []);
      })
      .catch(() => toast.error('Could not load sites or presets'));
  }, []);

  // KPI fetch on range/site change
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    reportsIntelApi
      .getKpis(range, site)
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) toast.error('Could not load KPIs'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [range, site]);

  // Budget overlay fetch — current month variance snapshot, used to enrich KpiCards
  const [budgetByKey, setBudgetByKey] = useState({});
  useEffect(() => {
    let cancelled = false;
    const now = new Date();
    reportsIntelApi
      .getBudgetVariance(site, now.getUTCFullYear(), now.getUTCMonth() + 1)
      .then((res) => {
        if (cancelled) return;
        const map = {};
        const monthProgress = res.data.month_progress_pct ?? null;
        (res.data.rows || []).forEach((r) => {
          if (r.monthly_target !== null && r.monthly_target !== undefined) {
            map[r.key] = {
              monthly_target: r.monthly_target,
              prorated_target: r.prorated_target,
              variance_pct: r.variance_pct,
              status: r.status,
              month_progress_pct: monthProgress,
            };
          }
        });
        setBudgetByKey(map);
      })
      .catch(() => { /* silent — budgets are optional */ });
    return () => { cancelled = true; };
  }, [site]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      const res = await reportsIntelApi.getKpis(range, site);
      setData(res.data);
      toast.success('Refreshed');
    } catch {
      toast.error("Couldn't refresh");
    } finally {
      setRefreshing(false);
    }
  };

  // Re-order KPIs per active preset
  const activePreset = presets.find((p) => p.id === activePresetId) || presets[0];
  const orderedKpis = useMemo(() => {
    if (!data) return [];
    if (!activePreset?.layout?.length) return data.kpis;
    const byKey = Object.fromEntries(data.kpis.map((k) => [k.key, k]));
    const ordered = activePreset.layout.map((key) => byKey[key]).filter(Boolean);
    const remaining = data.kpis.filter((k) => !activePreset.layout.includes(k.key));
    return [...ordered, ...remaining];
  }, [data, activePreset]);

  // Pass other KPI context (labour/wastage/food gp) into AI insights for richer reasoning
  const sharedContext = useMemo(() => {
    if (!data) return null;
    const m = Object.fromEntries(data.kpis.map((k) => [k.key, k.value]));
    return {
      labour_pct: m.labour_pct,
      food_gp_pct: m.food_gp_pct,
      drink_gp_pct: m.drink_gp_pct,
      wastage_pct: m.wastage_pct,
      avg_spend: m.avg_spend,
      covers: m.covers,
    };
  }, [data]);

  const savePreset = async () => {
    if (!newPresetName.trim()) {
      toast.error('Give your preset a name');
      return;
    }
    try {
      const res = await reportsIntelApi.createPreset({
        name: newPresetName.trim(),
        layout: orderedKpis.map((k) => k.key),
      });
      const fresh = await reportsIntelApi.listPresets();
      setPresets(fresh.data.presets || []);
      setActivePresetId(res.data.id);
      setPresetDialogOpen(false);
      setNewPresetName('');
      toast.success('Preset saved');
    } catch {
      toast.error('Could not save preset');
    }
  };

  return (
    <ReportsLayout>
      {/* Exec header — site, range, presets, refresh */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <SitePicker
            sites={sites}
            value={site || 'all'}
            onChange={(v) => setSite(v === 'all' ? '' : v)}
            allowAll
            triggerClassName="h-9 min-w-[240px]"
            testId="exec-site-select"
          />

          <Segmented
            value={range}
            onChange={setRange}
            options={RANGES.map((r) => ({ value: r.key, label: r.label, testId: `exec-range-${r.key}` }))}
            data-testid="exec-range-switcher"
          />

          <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={refresh} data-testid="exec-refresh-btn">
            <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
            Refresh
          </Button>

        </div>

        {/* Presets row */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] uppercase tracking-wider font-semibold text-ink-faint">Presets</span>
          {presets.map((p) => (
            <OptionChip
              key={p.id}
              selected={activePresetId === p.id}
              onClick={() => setActivePresetId(p.id)}
              className="h-7 rounded-full px-2.5 text-[12px]"
              data-testid={`exec-preset-${p.id}`}
            >
              {p.name}
            </OptionChip>
          ))}
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-[12.5px] gap-1 text-ink-soft hover:text-verlo"
            onClick={() => setPresetDialogOpen(true)}
            data-testid="exec-new-preset-btn"
          >
            <Plus size={12} />
            New preset
          </Button>
        </div>
      </div>

      {/* Variance Brief — group-level outlier digest */}

      {/* Alerts */}
      <AlertsBanner site={site} />

      {/* KPI grid */}
      {loading && !data ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5" data-testid="kpi-loading">
          {Array.from({ length: 14 }).map((_, i) => (
            <div key={i} className="h-32 bg-white border border-hairline rounded-lg animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5" data-testid="kpi-grid">
          {orderedKpis.map((kpi) => (
            <KpiCard
              key={kpi.key}
              kpi={kpi}
              range={range}
              siteLabel={siteLabel}
              context={sharedContext}
              budget={budgetByKey[kpi.key]}
              onExpand={(k) => setDrillKpi(k)}
              compareLabel={data.compare_label}
            />
          ))}
        </div>
      )}

      {/* Pinned custom views from Report Builder */}
      {pinnedViews.length > 0 && (
        <div className="mt-10" data-testid="exec-pinned-views">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-[15px] font-extrabold text-ink">Pinned</h3>
            <Link to="/back-office/reports/builder" className="text-[12.5px] text-verlo hover:underline font-semibold inline-flex items-center gap-0.5">
              Open Report builder
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {pinnedViews.map((v) => {
              // A dashboard has blocks, not one config: it has its own page and its own line.
              const board = v.version === 2;
              return (
                <Link
                  key={v.id}
                  to={`/back-office/reports/${board ? 'dashboards' : 'builder'}?view=${v.id}`}
                  className="block bg-white border border-hairline rounded-lg p-4 hover:bg-hover-tint"
                  data-testid={`exec-pinned-view-${v.id}`}
                >
                  <div className="font-heading text-[11px] font-bold uppercase tracking-[0.06em] text-ink-faint mb-1">
                    {board ? 'Dashboard' : 'Report'}
                  </div>
                  <div className="text-[13px] font-semibold text-ink truncate">{v.name}</div>
                  <div className="text-[12.5px] text-ink-soft mt-1">
                    {board
                      ? `${(v.layout || []).length} block${(v.layout || []).length === 1 ? '' : 's'}`
                      : <>
                          {v.config?.metrics?.length} metric(s)
                          {v.config?.dimension && ` · by ${v.config.dimension}`} · {RANGE_LABEL[v.config?.range] || v.config?.range}
                        </>}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Deep-dive shortcuts */}
      <div className="mt-10 flex flex-wrap gap-3">
        <Link
          to="/back-office/pnl"
          className="inline-flex items-center gap-2 text-[13px] font-semibold text-ink-soft hover:bg-hover-tint hover:text-ink bg-white border border-hairline rounded-lg px-3 py-2"
          data-testid="exec-deeplink-pnl"
        >
          Full P&amp;L overview
        </Link>
        <Link
          to="/back-office/sales"
          className="inline-flex items-center gap-2 text-[13px] font-semibold text-ink-soft hover:bg-hover-tint hover:text-ink bg-white border border-hairline rounded-lg px-3 py-2"
          data-testid="exec-deeplink-sales"
        >
          Sales detail
        </Link>
      </div>

      {/* Drill-down side sheet */}
      <Sheet open={!!drillKpi} onOpenChange={(o) => !o && setDrillKpi(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="drill-sheet">
          {drillKpi && (
            <>
              <SheetHeader>
                <SheetTitle className="text-base">{drillKpi.label}</SheetTitle>
                <SheetDescription className="text-[12px]">
                  {RANGE_LABEL[range]} · {siteLabel}
                </SheetDescription>
              </SheetHeader>
              <DrillPanelContent kpi={drillKpi} range={range} site={site} />
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* New preset dialog */}
      <Dialog open={presetDialogOpen} onOpenChange={setPresetDialogOpen}>
        <DialogContent data-testid="preset-dialog">
          <DialogHeader>
            <DialogTitle>Save current view as a preset</DialogTitle>
            <DialogDescription>
              Lock in this KPI arrangement so you can switch back to it in one click.
            </DialogDescription>
          </DialogHeader>
          <Input
            placeholder="e.g. Owner morning view"
            value={newPresetName}
            onChange={(e) => setNewPresetName(e.target.value)}
            maxLength={60}
            data-testid="preset-name-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setPresetDialogOpen(false)} data-testid="preset-cancel">
              Cancel
            </Button>
            <Button className="bg-verlo hover:bg-verlo-hover" onClick={savePreset} data-testid="preset-save">
              Save preset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ReportsLayout>
  );
};

export default ExecutiveDashboardPage;
